import { Fragment, type ReactNode } from 'react';
import type { PostEntities } from '@bb/shared';

const TOKEN = /(https?:\/\/\S+|[@#][A-Za-z0-9_]+)/g;

/**
 * Renders post text with entities picked out.
 *
 * t.co links are rewritten to their expanded display form: an opaque shortener
 * tells the reader nothing, and the whole point of this screen is scanning.
 */
export function PostText({ text, entities }: { text: string; entities: PostEntities }) {
  const expansions = new Map<string, { href: string; label: string }>();
  for (const entry of entities.urls) {
    expansions.set(entry.url, {
      href: entry.expandedUrl ?? entry.url,
      label: entry.displayUrl ?? entry.expandedUrl ?? entry.url,
    });
  }

  const parts = text.split(TOKEN);

  return (
    <p className="text-[14px] leading-[1.55] whitespace-pre-wrap break-words text-ink">
      {parts.map((part, index) => {
        if (!part) return null;
        const key = `${index}-${part}`;

        if (part.startsWith('http')) {
          const expanded = expansions.get(part);
          return (
            <a
              key={key}
              href={expanded?.href ?? part}
              target="_blank"
              rel="noreferrer noopener"
              className="text-info underline decoration-info/30 underline-offset-2 hover:decoration-info"
            >
              {expanded?.label ?? part}
            </a>
          );
        }

        if (part.startsWith('#')) {
          return (
            <span key={key} className="text-accent">
              {part}
            </span>
          );
        }

        if (part.startsWith('@')) {
          return (
            <span key={key} className="text-info">
              {part}
            </span>
          );
        }

        return <Fragment key={key}>{part as ReactNode}</Fragment>;
      })}
    </p>
  );
}
